import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LOGO_URL, formatApiError } from "../lib/api";
import { toast } from "sonner";

export function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = await login(email, password);
            toast.success("Welcome back, researcher.");
            const next = params.get("next");
            const role = data?.user?.role;
            // Only allow internal redirects (must start with /), and gate /admin on role.
            if (next && next.startsWith("/")) {
                if (next.startsWith("/admin") && role !== "admin") {
                    navigate("/account");
                } else {
                    navigate(next);
                }
            } else {
                navigate("/account");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <AuthShell title="Sign In" subtitle="Access your research account">
            <form onSubmit={submit} className="space-y-5">
                <div>
                    <label className="label">Email</label>
                    <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
                </div>
                <div>
                    <label className="label">Password</label>
                    <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input" />
                </div>
                <button type="submit" disabled={loading} data-testid="login-submit" className="btn btn-gold w-full">
                    {loading ? "Authenticating…" : "Sign In"}
                </button>
                <div className="flex justify-between data-label">
                    <Link to="/forgot-password" className="hover:text-brand">Forgot password?</Link>
                    <Link to={`/register${params.get("next") ? `?next=${encodeURIComponent(params.get("next"))}` : ""}`} className="hover:text-brand">Create account →</Link>
                </div>
            </form>
        </AuthShell>
    );
}

export function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [form, setForm] = useState({
        name: "", email: params.get("email") || "", password: "",
        organization: "", buyer_category: "researcher", research_field: "",
        age_21_plus: false, ruo_attestation: false, terms_accepted: false,
    });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.age_21_plus || !form.ruo_attestation || !form.terms_accepted) {
            toast.error("Please complete all three attestation checkboxes.");
            return;
        }
        setLoading(true);
        try {
            await register(form);
            toast.success("Research account verified. Welcome to Aminoplex.");
            const next = params.get("next");
            if (next && next.startsWith("/") && !next.startsWith("//")) {
                navigate(next);
            } else {
                navigate("/account");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <AuthShell title="Create Research Account" subtitle="Qualified researchers, professionals & laboratories only">
            <form onSubmit={submit} className="space-y-4">
                <div data-testid="ruo-notice" className="p-3 border border-[var(--brand)] bg-[var(--brand-soft)] text-xs font-mono text-primary leading-relaxed">
                    All products supplied by Aminoplex are strictly <strong className="text-brand">Research Use Only</strong> — not for human or animal consumption. To create an account, you must attest to these terms below.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="label">Legal Name</label>
                        <input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="input" />
                    </div>
                    <div>
                        <label className="label">Email</label>
                        <input data-testid="register-email" type="email" required autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input" />
                    </div>
                </div>

                <div>
                    <label className="label">Password · 6+ characters</label>
                    <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="input" />
                </div>

                <div>
                    <label className="label">Business / Entity Type</label>
                    <select data-testid="register-org" required value={form.organization} onChange={(e) => set("organization", e.target.value)} className="input">
                        <option value="">Select entity type</option>
                        <option value="University / Academic Institution">University / Academic Institution</option>
                        <option value="Private Research Laboratory">Private Research Laboratory</option>
                        <option value="Biotechnology Company">Biotechnology Company</option>
                        <option value="Pharmaceutical Company">Pharmaceutical Company</option>
                        <option value="Government Research Facility">Government Research Facility</option>
                        <option value="Contract Research Organization">Contract Research Organization</option>
                        <option value="Independent Qualified Researcher">Independent Qualified Researcher</option>
                        <option value="Other Qualified Research Entity">Other Qualified Research Entity</option>
                    </select>
                </div>

                <div>
                    <label className="label">Buyer Category</label>
                    <select data-testid="register-category" value={form.buyer_category} onChange={(e) => set("buyer_category", e.target.value)} className="input">
                        <option value="researcher">Independent Researcher</option>
                        <option value="professional">Research Professional</option>
                        <option value="laboratory">Laboratory Purchaser</option>
                        <option value="institution">Academic / Institutional Buyer</option>
                    </select>
                </div>

                <div className="space-y-3 pt-2">
                    <label className="flex items-start gap-3 text-xs text-secondary cursor-pointer">
                        <input data-testid="attest-age" type="checkbox" required checked={form.age_21_plus} onChange={(e) => set("age_21_plus", e.target.checked)} className="mt-0.5" />
                        <span>I confirm I am at least <strong className="text-primary">21 years of age</strong>.</span>
                    </label>
                    <label className="flex items-start gap-3 text-xs text-secondary cursor-pointer">
                        <input data-testid="attest-ruo" type="checkbox" required checked={form.ruo_attestation} onChange={(e) => set("ruo_attestation", e.target.checked)} className="mt-0.5" />
                        <span>I attest that products purchased are exclusively for <strong className="text-primary">legitimate in-vitro laboratory research</strong> and <strong className="text-primary">will NOT be used for human or animal consumption</strong>.</span>
                    </label>
                    <label className="flex items-start gap-3 text-xs text-secondary cursor-pointer">
                        <input data-testid="attest-terms" type="checkbox" required checked={form.terms_accepted} onChange={(e) => set("terms_accepted", e.target.checked)} className="mt-0.5" />
                        <span>I have read and accept the <Link to="/terms" className="link-gold" target="_blank">Terms & Conditions</Link>, <Link to="/privacy" className="link-gold" target="_blank">Privacy Policy</Link>, and <Link to="/compliance" className="link-gold" target="_blank">Research-Use-Only Compliance Policy</Link>.</span>
                    </label>
                </div>

                <button type="submit" disabled={loading} data-testid="register-submit" className="btn btn-gold w-full">
                    {loading ? "Verifying…" : "Create Research Account"}
                </button>
                <Link to={`/login${params.get("next") ? `?next=${encodeURIComponent(params.get("next"))}` : ""}`} className="block text-center data-label hover:text-brand">Already have an account? Sign in →</Link>
            </form>
        </AuthShell>
    );
}

export function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [devToken, setDevToken] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { api } = await import("../lib/api");
            const { data } = await api.post("/auth/forgot-password", { email });
            setSent(true);
            if (data.dev_token) setDevToken(data.dev_token);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    return (
        <AuthShell title="Recover Password" subtitle="We'll generate a reset link">
            {sent ? (
                <div className="space-y-4">
                    <p className="text-secondary text-sm">If <span className="text-primary">{email}</span> is registered, a reset link will be available.</p>
                    {devToken && (
                        <div className="card p-4">
                            <div className="data-label text-brand mb-2">DEV RESET TOKEN</div>
                            <pre className="font-mono text-xs text-secondary break-all">{devToken}</pre>
                            <Link to={`/reset-password?token=${devToken}`} className="btn btn-gold w-full mt-4">Continue to Reset</Link>
                        </div>
                    )}
                    <Link to="/login" className="block text-center data-label hover:text-brand">← Back to Sign In</Link>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-5">
                    <div>
                        <label className="label">Email</label>
                        <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
                    </div>
                    <button type="submit" disabled={loading} data-testid="forgot-submit" className="btn btn-gold w-full">
                        {loading ? "Sending…" : "Generate Reset Link"}
                    </button>
                </form>
            )}
        </AuthShell>
    );
}

export function ResetPasswordPage() {
    const [params] = useSearchParams();
    const [token, setToken] = useState(params.get("token") || "");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { api } = await import("../lib/api");
            await api.post("/auth/reset-password", { token, password });
            toast.success("Password updated · sign in");
            navigate("/login");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <AuthShell title="Reset Password" subtitle="Choose a new password">
            <form onSubmit={submit} className="space-y-5">
                <div>
                    <label className="label">Reset Token</label>
                    <input value={token} onChange={(e) => setToken(e.target.value)} className="input" />
                </div>
                <div>
                    <label className="label">New Password · 6+ chars</label>
                    <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input" />
                </div>
                <button type="submit" disabled={loading} className="btn btn-gold w-full">
                    {loading ? "Updating…" : "Update Password"}
                </button>
            </form>
        </AuthShell>
    );
}

function AuthShell({ title, subtitle, children }) {
    return (
        <div className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-md mx-auto px-6">
                <div className="text-center mb-10">
                    <img src={LOGO_URL} alt="Aminoplex" className="h-16 w-16 mx-auto mb-6" />
                    <div className="data-label text-brand mb-3">AMINOPLEX RESEARCH</div>
                    <h1 className="font-serif text-4xl">{title}</h1>
                    <p className="text-secondary text-sm mt-2">{subtitle}</p>
                </div>
                <div className="card p-8">{children}</div>
            </div>
        </div>
    );
}
